import { Route } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRpPaymentGatewaysConfig, useSetPaymentsApiRouting } from '@/hooks/use-rp-payment-gateways-config';
import { toast } from '@/stores/toast-store';

/**
 * Super-admin switch that routes one location's ACH through the payments-api
 * service instead of the module calling iStream directly. Sits under the
 * Receive Payments / Send Payments module gates, one per direction, because the
 * two are independent — a location can move its receives over while its payouts
 * stay on the direct path.
 *
 * Backed by rp_merchant_payment_configs.use_payments_api on that location's
 * ACTIVE ACH row (RECEIVE and SEND are separate rows, so the flag is already
 * per-direction). Default OFF.
 *
 * Only iStream is routable today, and only when an ACH gateway actually exists,
 * so the switch renders disabled with the reason rather than silently missing —
 * an unconfigured location otherwise leaves the admin hunting for a control that
 * was never drawn.
 */
export interface PaymentsApiRoutingSwitchProps {
  tenantId: string;
  merchantId?: string | null;
  direction: 'RECEIVE' | 'SEND';
}

export function PaymentsApiRoutingSwitch({ tenantId, merchantId, direction }: PaymentsApiRoutingSwitchProps) {
  const { data, isLoading } = useRpPaymentGatewaysConfig(tenantId, merchantId || undefined);
  const setRouting = useSetPaymentsApiRouting();

  if (!merchantId || isLoading) return null;

  const achConfig = (data?.data || []).find((c) => c.type === direction && c.paymentMethod === 'ACH / Bank');

  const enabled = achConfig?.usePaymentsApi === true;
  const isIStream = achConfig?.paymentsApi === 'iStream';
  const blockedReason = !achConfig
    ? 'Configure the ACH / Bank gateway for this location first'
    : !isIStream
      ? `Only available for iStream ACH (currently ${achConfig.paymentsApi})`
      : null;

  const onToggle = () => {
    if (blockedReason) return;
    const next = !enabled;
    setRouting.mutate(
      { tenantId, merchantId, type: direction, usePaymentsApi: next },
      {
        onSuccess: () =>
          toast.success(
            next
              ? `${direction === 'SEND' ? 'Payouts' : 'Receives'} now route through Payments API`
              : `${direction === 'SEND' ? 'Payouts' : 'Receives'} back on direct iStream`,
          ),
        onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to update routing'),
      },
    );
  };

  return (
    <div className="mt-3 space-y-2 border-t border-border/60 pt-3" data-testid={`payments-api-routing-${direction}`}>
      <div className="flex items-center justify-between rounded-md bg-white/60 px-2 py-1.5">
        <div className="flex min-w-0 items-center gap-2">
          <Route className="h-3.5 w-3.5 text-primary" />
          <div className="min-w-0">
            <div className="text-xs font-medium">Route ACH through Payments API</div>
            <div className="text-[11px] text-muted-foreground">
              {blockedReason ? (
                <span>{blockedReason}</span>
              ) : enabled ? (
                <span className="text-green-600">
                  {direction === 'SEND' ? 'Payouts go' : 'Receives go'} through payments-api — recorded centrally,
                  status by webhook
                </span>
              ) : (
                <span>
                  {direction === 'SEND' ? 'Payouts call' : 'Receives call'} iStream directly, as today (default)
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={`Route ${direction === 'SEND' ? 'payouts' : 'receives'} through Payments API`}
          title={blockedReason || (enabled ? 'Switch back to direct iStream' : 'Route through payments-api')}
          onClick={onToggle}
          disabled={setRouting.isPending || !!blockedReason}
          className={cn(
            'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            enabled ? 'bg-primary' : 'bg-gray-200',
          )}
        >
          <span
            className={cn(
              'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
              enabled ? 'translate-x-4' : 'translate-x-0.5',
            )}
          />
        </button>
      </div>
    </div>
  );
}
